import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "store/store";
import UserManagementState from "interfaces/state/UserManagementState";
import UserRequest from "interfaces/request/UserRequest";
import UserResponse from "interfaces/response/UserResponse";
import StripeCustomerResponse from "interfaces/response/StripeCustomerResponse";
import { toastSuccess } from "components/common/ToastNotification";
import PagingParameter from "interfaces/request/PagingParameter";
import { DataGridPageSize } from "../../../src/shared/constants/GridConstantValues";
import {
  getAllUsersAsync,
  createPortalUserAsync,
  updatePortalUserAsync,
  deleteUserAsync,
  createStripeCustomerAsync 
} from "apis/UserManagementApi";

const initialState: UserManagementState = {
  selectedUserDetail: null, // Currently selected user from grid
  users: [], // All users to show in grid
  loading: false,
  totalItemsCount: 0, // Total number of users. Need this for pagination in customeDataStore
};

const insertNewUserInState = (newUser: UserResponse, state: UserManagementState) => {
  // If the size of current array is equal to the pagesize then we are removing the last element
  // from array and inserting new one in the top
  if (state.users.length === DataGridPageSize) {
    state.users.pop();
  }
  state.users.unshift(newUser);
}
// If current user has no stripe account, then we are creating stripe account for current user
// and updating the state in both users array and selectedUser object
const updateStripeCustomerId = (stripeCustomerDetails: StripeCustomerResponse, state: UserManagementState) => {
  const customerToBeUpdated: number = stripeCustomerDetails.userId;
  const existingCustomerIndex = state.users.findIndex(
    (user: UserResponse) => user.id === customerToBeUpdated
  );
  if (existingCustomerIndex > -1) {
    state.users[existingCustomerIndex].customerId = stripeCustomerDetails.customerId;
    state.users[existingCustomerIndex].isRegisteredOnStripe = true;

    state.selectedUserDetail = state.users[existingCustomerIndex];
  }
};

const updateUserInState = (
  UserToBeUpdated: number,
  UserDetails: UserResponse,
  state: UserManagementState
) => {
  const existingUserIndex = state.users.findIndex(
    (user: UserResponse) => user.id === UserToBeUpdated
  );
  if (existingUserIndex > -1) {
    state.users[existingUserIndex] = UserDetails;
  }
};

const changeUserToInactive = (userId: number, user: UserResponse, state: UserManagementState) => {
  const existingUserIndex = state.users.findIndex((user: UserResponse) => user.id === userId);

  if (existingUserIndex > -1) {
    state.users[existingUserIndex].isActive = user.isActive;
    state.users[existingUserIndex].inactivatedOn = user.inactivatedOn;
  }
}

export const loadUserRelatedDataAsync = createAsyncThunk<any, void, { state: RootState }>
  ("usermanagement/loadUserRelatedDataAsync", async (_, thunkAPI) => {
    const getUsersPromise = thunkAPI.dispatch(getAllUsersListAsync());

    return Promise.all([getUsersPromise]);
});

export const createStripeCustomerForSelectedUserAsync = createAsyncThunk(
  "usermanagement/createStripeCustomerForSelectedUserAsync",
  async (request: any) => {
    return await createStripeCustomerAsync(request.userId);
  }
);
// Need to find a way to use same names
export const getAllUsersListAsync = createAsyncThunk(
  "usermanagement/getAllUsersListAsync",
  async (pagingParameter?: PagingParameter) => {
    const response = await getAllUsersAsync(pagingParameter?.currentPage, pagingParameter?.pageSize);
    return response;
  }
);

export const createNewUserAsync = createAsyncThunk(
  "usermanagement/createNewUserAsync",
  async (postData: UserRequest) => {
    return await createPortalUserAsync(postData);
  }
);

export const updatePortalUserDetailAsync = createAsyncThunk<
  UserResponse,
  { userId: number; userRequest: UserRequest }
>("usermanagement/updatePortalUserDetailAsync", async (request) => {
  return await updatePortalUserAsync(request.userId, request.userRequest);
});

export const deleteUser = createAsyncThunk(
  "usermanagement/deleteUser",
  async (userId: number) => {
    return await deleteUserAsync(userId);
  }
);

export const userManagementSlice = createSlice({
  name: "usermanagement",
  initialState,
  reducers: {
    setSelectedUser: (state, action) => {
      state.selectedUserDetail = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllUsersListAsync.pending, (state) => {
      state.users = [];
      state.loading = true;
    });
    builder.addCase(getAllUsersListAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.users = action.payload.records;
      state.totalItemsCount = action.payload.totalItemCount;
    });
    builder.addCase(getAllUsersListAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(createNewUserAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createNewUserAsync.fulfilled, (state, action) => {
      state.loading = false;
      insertNewUserInState(action.payload, state);
    });
    builder.addCase(createNewUserAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updatePortalUserDetailAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updatePortalUserDetailAsync.fulfilled, (state, action) => {
      updateUserInState(action.payload.id, action.payload, state);
      toastSuccess("User detail updated.");
      state.loading = false;
    });
    builder.addCase(updatePortalUserDetailAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteUser.fulfilled, (state, action) => {
      changeUserToInactive(action.payload.id, action.payload, state);
      state.loading = false;
    });
    builder.addCase(deleteUser.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(createStripeCustomerForSelectedUserAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createStripeCustomerForSelectedUserAsync.fulfilled, (state, action) => {
      updateStripeCustomerId(action.payload, state);
      toastSuccess("Stripe customer created.");
      state.loading = false;
    });
    builder.addCase(createStripeCustomerForSelectedUserAsync.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const { setSelectedUser } = userManagementSlice.actions;
export default userManagementSlice.reducer;